import { Box, TextInput, Textarea, Button, Text, Flex, Badge, MultiSelect } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useState } from "react";
import { getAdditionalTechInfraOptions } from "../../../components/shared/constants/general.const";

interface CapabilitiesOfferingsProps {
  formData: {
    coreCapabilities: string[];
    secondaryCapabilities: string[];
    keyDifferentiators: string;
    technologies: string[];
    additionalTechInfrastructure: string[];
  };
  updateForm: (field: string, value: any) => void;
  coreCapabilityInput: string;
  setCoreCapabilityInput: (value: string) => void;
  secondaryCapabilityInput: string;
  setSecondaryCapabilityInput: (value: string) => void;
  technologyInput: string;
  setTechnologyInput: (value: string) => void;
  addTag: (field: "coreCapabilities" | "secondaryCapabilities" | "technologies", value: string) => void;
  removeTag: (field: "coreCapabilities" | "secondaryCapabilities" | "technologies", index: number) => void;
  disabled?: boolean;
}

export default function CapabilitiesOfferings({
  formData,
  updateForm,
  coreCapabilityInput,
  setCoreCapabilityInput,
  secondaryCapabilityInput,
  setSecondaryCapabilityInput,
  technologyInput,
  setTechnologyInput,
  removeTag,
  disabled,
}: CapabilitiesOfferingsProps) {
  const [coreCapabilityError, setCoreCapabilityError] = useState<string | null>(null);
  const [secondaryCapabilityError, setSecondaryCapabilityError] = useState<string | null>(null);
  const [technologyError, setTechnologyError] = useState<string | null>(null);

  const handleAddCoreCapability = () => {
    if (formData.coreCapabilities.length >= 10) {
      setCoreCapabilityError("Maximum 10 core capabilities allowed");
      return;
    }
    if (coreCapabilityInput.trim()) {
      setCoreCapabilityError(null);

      // Split by comma and process each value
      const values = coreCapabilityInput
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v);
      const remainingSlots = 10 - formData.coreCapabilities.length;

      if (values.length > remainingSlots) {
        setCoreCapabilityError(`Only ${remainingSlots} more capabilities can be added`);
        return;
      }

      // Filter out duplicates and add all values at once
      const uniqueNewValues = values.filter((v) => !formData.coreCapabilities.includes(v));
      updateForm("coreCapabilities", [...formData.coreCapabilities, ...uniqueNewValues]);

      setCoreCapabilityInput("");
    }
  };

  const handleAddSecondaryCapability = () => {
    if (formData.secondaryCapabilities.length >= 10) {
      setSecondaryCapabilityError("Maximum 10 secondary capabilities allowed");
      return;
    }
    if (secondaryCapabilityInput.trim()) {
      setSecondaryCapabilityError(null);

      // Split by comma and process each value
      const values = secondaryCapabilityInput
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v);
      const remainingSlots = 10 - formData.secondaryCapabilities.length;

      if (values.length > remainingSlots) {
        setSecondaryCapabilityError(`Only ${remainingSlots} more capabilities can be added`);
        return;
      }

      // Filter out duplicates and add all values at once
      const uniqueNewValues = values.filter((v) => !formData.secondaryCapabilities.includes(v));
      updateForm("secondaryCapabilities", [...formData.secondaryCapabilities, ...uniqueNewValues]);

      setSecondaryCapabilityInput("");
    }
  };

  const handleAddTechnology = () => {
    if (formData.technologies.length >= 10) {
      setTechnologyError("Maximum 10 technologies allowed");
      return;
    }
    if (technologyInput.trim()) {
      setTechnologyError(null);

      // Split by comma and process each value
      const values = technologyInput
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v);
      const remainingSlots = 10 - formData.technologies.length;

      if (values.length > remainingSlots) {
        setTechnologyError(`Only ${remainingSlots} more technologies can be added`);
        return;
      }

      // Filter out duplicates and add all values at once
      const uniqueNewValues = values.filter((v) => !formData.technologies.includes(v));
      updateForm("technologies", [...formData.technologies, ...uniqueNewValues]);

      setTechnologyInput("");
    }
  };

  return (
    <Box>
      {/* Core Capabilities */}
      <Text fz="sm" fw={500} mb={4}>
        Core Capabilities
        <Text span c="red" fz="lg">
          {" "}
          *
        </Text>{" "}
        <Text span c="slate.6">
          ({formData.coreCapabilities.length} / 10)
        </Text>
      </Text>
      <Text fz="xs" c={coreCapabilityError ? "red" : "slate.6"} mb="xs">
        {coreCapabilityError || "Add up to 10 core capabilities. Press Enter or use comma-separated values"}
      </Text>
      <Flex gap="sm" mb="sm">
        <TextInput
          placeholder="eg: CNC Machining, Cold Forging, Heat Treatment"
          value={coreCapabilityInput}
          onChange={(e) => {
            setCoreCapabilityInput(e.currentTarget.value);
            setCoreCapabilityError(null);
          }}
          style={{ flex: 1 }}
          disabled={disabled}
          error={coreCapabilityError ? true : false}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddCoreCapability();
            }
          }}
        />
        <Button variant="outline" size="sm" disabled={disabled} onClick={handleAddCoreCapability}>
          Add
        </Button>
      </Flex>
      <Flex gap="xs" wrap="wrap" mb="xs">
        {formData.coreCapabilities.map((cap, index) => (
          <Badge
            key={index}
            variant="light"
            rightSection={
              !disabled && (
                <IconX
                  size={12}
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    removeTag("coreCapabilities", index);
                    setCoreCapabilityError(null);
                  }}
                />
              )
            }
          >
            {cap}
          </Badge>
        ))}
      </Flex>

      {/* Secondary Capabilities */}
      <Text fz="sm" fw={500} mb={4} mt="md">
        Secondary Capabilities (Optional){" "}
        <Text span c="slate.6">
          ({formData.secondaryCapabilities.length} / 10)
        </Text>
      </Text>
      <Text fz="xs" c={secondaryCapabilityError ? "red" : "slate.6"} mb="xs">
        {secondaryCapabilityError || "Add up to 10 secondary capabilities. Press Enter or use comma-separated values"}
      </Text>
      <Flex gap="sm" mb="sm">
        <TextInput
          placeholder="Additional capabilities, e.g. Custom Packaging, Quality Inspection, OEM Support"
          value={secondaryCapabilityInput}
          onChange={(e) => {
            setSecondaryCapabilityInput(e.currentTarget.value);
            setSecondaryCapabilityError(null);
          }}
          style={{ flex: 1 }}
          disabled={disabled}
          error={secondaryCapabilityError ? true : false}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddSecondaryCapability();
            }
          }}
        />
        <Button variant="outline" disabled={disabled} size="sm" onClick={handleAddSecondaryCapability}>
          Add
        </Button>
      </Flex>
      <Flex gap="xs" wrap="wrap" mb="xs">
        {formData.secondaryCapabilities.map((cap, index) => (
          <Badge
            key={index}
            variant="light"
            rightSection={
              !disabled && (
                <IconX
                  size={12}
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    removeTag("secondaryCapabilities", index);
                    setSecondaryCapabilityError(null);
                  }}
                />
              )
            }
          >
            {cap}
          </Badge>
        ))}
      </Flex>

      {/* Key Differentiators */}
      <Textarea
        label="Key Differentiators (Optional)"
        placeholder="What sets you apart - eg: In-house tooling & design capabilities, Flexible MOQs, Advanced quality control and inspection systems."
        minRows={3}
        value={formData.keyDifferentiators}
        onChange={(e) => updateForm("keyDifferentiators", e.currentTarget.value)}
        disabled={disabled}
        mb="lg"
      />

      {/* Technologies */}
      <Text fz="sm" fw={500} mb={4}>
        Technology & Infrastructure (Optional){" "}
        <Text span c="slate.6">
          ({formData.technologies.length} / 10)
        </Text>
      </Text>
      <Text fz="xs" c={technologyError ? "red" : "slate.6"} mb="xs">
        {technologyError || "Add up to 10 technologies. Press Enter or use comma-separated values"}
      </Text>
      <Flex gap="sm" mb="sm">
        <TextInput
          placeholder="eg : Injection molding, CNC machining, Robotics & automation"
          value={technologyInput}
          onChange={(e) => {
            setTechnologyInput(e.currentTarget.value);
            setTechnologyError(null);
          }}
          style={{ flex: 1 }}
          disabled={disabled}
          error={technologyError ? true : false}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddTechnology();
            }
          }}
        />

        <Button variant="outline" disabled={disabled} size="sm" onClick={handleAddTechnology}>
          Add
        </Button>
      </Flex>
      <Flex gap="xs" wrap="wrap" mb="xs">
        {formData.technologies.map((tech, index) => (
          <Badge
            key={index}
            variant="light"
            rightSection={
              !disabled && (
                <IconX
                  size={12}
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    removeTag("technologies", index);
                    setTechnologyError(null);
                  }}
                />
              )
            }
          >
            {tech}
          </Badge>
        ))}
      </Flex>

      {/* Additional Technology & Infrastructure */}
      <Text fz="sm" fw={500} mb={4} mt="md">
        Additional Technology & Infrastructure (Optional)
      </Text>
      <Text fz="xs" c="slate.6" mb="xs">
        Select all facilities and infrastructure your company has in-house
      </Text>
      <MultiSelect
        data={getAdditionalTechInfraOptions()}
        value={formData.additionalTechInfrastructure}
        onChange={(value) => updateForm("additionalTechInfrastructure", value)}
        placeholder="Select applicable options"
        searchable
        disabled={disabled}
        mb="xs"
      />
    </Box>
  );
}
